import React, { Component, FC } from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { ProgressBar } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'

import { Project } from '../../../models3/Project'
import { getPercentCompleteThroughStage } from './ProgressUtils'
import { ColorSquare } from '../../utils/Buttons'
import './ProgressReport.css'
import { Root } from '../../../models3/Root'
import dateLocales from './DateLocales'
import { RootConsumer } from '../../app/RootContext'

interface IOverlappingProgressBar {
    beginPercent: number
    endPercent: number
}

const OverlappingProgressBar: FC<IOverlappingProgressBar> = (props) => {
    const { beginPercent, endPercent } = props
    return (
        <ProgressBar className="progress-bar-custom">
            <ProgressBar bsStyle="success" now={beginPercent} />
            <ProgressBar bsStyle="info" now={endPercent - beginPercent} />
        </ProgressBar>
    )
}

interface IProgressTableLegend {
    beginDate: Date
    endDate: Date
    beginColor: string
    endColor: string
}

const ProgressTableLegend: FC<IProgressTableLegend> = (props) => {
    const { t } = useTranslation()

    return (
        <RootConsumer>
            {(rt) => {
                if (!rt) return null

                const { dateFormatter } = rt
                const { beginDate, endDate, beginColor, endColor } = props
                const formattedBeginDate = dateFormatter.format(beginDate)
                const formattedEndDate = dateFormatter.format(endDate)
                return (
                    <div>
                        <div>
                            <ColorSquare color={beginColor} width="20px" height="20px" />
                            {t('Progress as of {{date}}', { date: formattedBeginDate })}
                        </div>
                        <div>
                            <ColorSquare color={endColor} width="20px" height="20px" />
                            {t('Progress as of {{date}}', { date: formattedEndDate })}
                        </div>
                    </div>
                )
            }}
        </RootConsumer>
    )
}

interface IProgressTable {
    rt: Root
    project: Project
    byPortion: boolean
    beginDate: Date
    endDate: Date
}

const ProgressTable: FC<IProgressTable> = (props) => {
    const { t } = useTranslation()

    const { rt, project, byPortion, beginDate, endDate } = props
    const { portions, plans } = project

    const allPassages = portions.map((portion) => portion.passages).reduce((a, acc) => a.concat(acc), [])
    const showItems = allPassages.filter((e) => e.difficulty > 0).length > 0
    const plan = plans[0]
    const viewableStages = plan?.viewableStages || []

    return (
        <div>
            {showItems ? (
                <div>
                    <div className="progress-table-legend">
                        <ProgressTableLegend
                            beginDate={beginDate}
                            endDate={endDate}
                            beginColor="green"
                            endColor="deepskyblue"
                        />
                    </div>
                    <table>
                        <tbody>
                            <tr>
                                <th className="progress-table-item" />
                                {viewableStages.map((stage) => (
                                    <th key={`${stage.name}`} className="progress-table-item">
                                        {stage.displayedName}
                                    </th>
                                ))}
                            </tr>
                        </tbody>
                        {byPortion
                            ? portions.map((portion) => {
                                  const shouldShow =
                                      portion.passages.filter((passage) => passage.difficulty > 0).length > 0
                                  return shouldShow ? (
                                      <tbody key={portion._id}>
                                          <tr>
                                              <td className="progress-table-item">
                                                  <Link
                                                      to="/index.html"
                                                      onClick={() => {
                                                          rt.selectPage('/')
                                                          rt.setPortion(portion)
                                                      }}
                                                  >
                                                      {portion.name}
                                                  </Link>
                                              </td>
                                              {viewableStages.map((stage) => {
                                                  const { passages } = portion
                                                  const beginPercent =
                                                      getPercentCompleteThroughStage(passages, plan, stage, beginDate) *
                                                      100
                                                  const endPercent =
                                                      getPercentCompleteThroughStage(passages, plan, stage, endDate) *
                                                      100
                                                  return (
                                                      <td
                                                          key={`${stage.name}-${portion._id}`}
                                                          className="progress-table-item progress-report-progress"
                                                      >
                                                          <OverlappingProgressBar
                                                              beginPercent={beginPercent}
                                                              endPercent={endPercent}
                                                          />
                                                      </td>
                                                  )
                                              })}
                                          </tr>
                                      </tbody>
                                  ) : null
                              })
                            : portions.map((portion) => {
                                  const shouldShow =
                                      portion.passages.filter((passage) => passage.difficulty > 0).length > 0
                                  return shouldShow ? (
                                      <tbody key={portion._id}>
                                          <tr>
                                              <td className="progress-table-item">
                                                  <Link
                                                      to="/index.html"
                                                      onClick={() => {
                                                          rt.selectPage('/')
                                                          rt.setPortion(portion)
                                                      }}
                                                  >
                                                      <b>{portion.name}</b>
                                                  </Link>
                                              </td>
                                              {viewableStages.map((stage) => (
                                                  <td
                                                      key={stage._id}
                                                      className="progress-table-item progress-report-progress"
                                                  />
                                              ))}
                                          </tr>
                                          {portion.passages.map((passage) => (
                                              <tr key={`${portion._id}-${passage._id}`}>
                                                  <td className="progress-table-item">
                                                      &nbsp;&nbsp;&nbsp;&nbsp;{`${passage.name}`}
                                                  </td>
                                                  {viewableStages.map((stage) => {
                                                      const beginPercent =
                                                          getPercentCompleteThroughStage(
                                                              [passage],
                                                              plan,
                                                              stage,
                                                              beginDate
                                                          ) * 100
                                                      const endPercent =
                                                          getPercentCompleteThroughStage(
                                                              [passage],
                                                              plan,
                                                              stage,
                                                              endDate
                                                          ) * 100
                                                      return (
                                                          <td
                                                              key={`${stage.name}-${portion._id}-${passage._id}`}
                                                              className="progress-table-item progress-report-progress"
                                                          >
                                                              <OverlappingProgressBar
                                                                  beginPercent={beginPercent}
                                                                  endPercent={endPercent}
                                                              />
                                                          </td>
                                                      )
                                                  })}
                                              </tr>
                                          ))}
                                          <tr className="blank-table-row" />
                                      </tbody>
                                  ) : null
                              })}
                        <tbody>
                            <tr key="total">
                                <td className="progress-table-item">{t('TOTAL')}</td>
                                {viewableStages.map((stage) => {
                                    const passages = project.portions.flatMap((portion) => portion.passages)
                                    const beginPercent =
                                        getPercentCompleteThroughStage(passages, plan, stage, beginDate) * 100
                                    const endPercent =
                                        getPercentCompleteThroughStage(passages, plan, stage, endDate) * 100
                                    return (
                                        <td key={stage._id} className="progress-table-item progress-report-progress">
                                            <OverlappingProgressBar
                                                beginPercent={beginPercent}
                                                endPercent={endPercent}
                                            />
                                        </td>
                                    )
                                })}
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>{t('There are no passages with nonzero difficulty.')}</p>
            )}
        </div>
    )
}

interface IReportBy {
    currentlySelected: string
    onSelect: (item: string) => void
}

const ReportBy: FC<IReportBy> = (props) => {
    const { t } = useTranslation()

    const { currentlySelected, onSelect } = props
    return (
        <div>
            <select
                className="custom-select progress-report-dropdown-menu"
                data-id="progress-report-dropdown-menu"
                value={currentlySelected}
                onChange={(e) => onSelect(e.target.value)}
            >
                <option key="portion" value="portion">
                    {t('Portion')}
                </option>
                <option key="passage" value="passage">
                    {t('Passage')}
                </option>
            </select>
        </div>
    )
}
interface IProgressReportHeader {
    projectName: string
    reportBy: string
    setReportBy: (item: string) => void
    startDate: Date
    endDate: Date
    setStartDate: (date: Date) => void
    setEndDate: (date: Date) => void
    rt: Root
}

const ProgressReportHeader: FC<IProgressReportHeader> = ({
    rt,
    projectName,
    reportBy,
    setReportBy,
    startDate,
    endDate,
    setStartDate,
    setEndDate
}) => {
    const { t } = useTranslation()

    const { uiLanguage } = rt
    const { dateFormat } = rt.project
    const localeData = dateLocales[rt.uiLanguage]
    registerLocale(rt.uiLanguage, localeData)

    return (
        <div className="progress-report-header">
            <h4 className="progress-report-project-name">
                <b>{projectName}</b>
            </h4>
            <div className="progress-report-controls">
                <div className="progress-report-header-item">
                    {t('Report by')}
                    <ReportBy currentlySelected={reportBy} onSelect={setReportBy} />
                </div>
                <div className="progress-report-header-item">
                    {t('Period begin date')}
                    <div>
                        <DatePicker
                            selected={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            locale={uiLanguage}
                            dateFormat={dateFormat}
                        />
                    </div>
                </div>
                <div className="progress-report-header-item">
                    {t('Period end date')}
                    <div>
                        <DatePicker
                            selected={endDate}
                            onChange={setEndDate}
                            minDate={startDate}
                            maxDate={new Date()}
                            locale={uiLanguage}
                            dateFormat={dateFormat}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

interface IProgressReport {
    rt: Root
}

class ProgressReport extends Component<IProgressReport> {
    @observable startDate: Date

    @observable endDate: Date

    @observable reportBy = `portion`

    constructor(props: IProgressReport) {
        super(props)
        const today = new Date()
        const todayYear = today.getFullYear()
        const quarters = [
            new Date(todayYear, 0),
            new Date(todayYear, 3),
            new Date(todayYear, 6),
            new Date(todayYear, 9)
        ]
        const todayMonth = today.getMonth()
        if (todayMonth < 3) {
            this.startDate = quarters[0]
        } else if (todayMonth < 6) {
            this.startDate = quarters[1]
        } else if (todayMonth < 9) {
            this.startDate = quarters[2]
        } else {
            this.startDate = quarters[3]
        }
        this.endDate = today
    }

    render() {
        const { rt } = this.props
        const { project } = rt
        const { displayName } = project
        const { reportBy, startDate, endDate } = this
        return (
            <div>
                <ProgressReportHeader
                    rt={rt}
                    projectName={displayName}
                    reportBy={reportBy}
                    setReportBy={(item) => (this.reportBy = item)}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={(date) => (this.startDate = date)}
                    setEndDate={(date) => (this.endDate = date)}
                />
                <div className="progress-report-separator" />
                <div className="progress-report-table">
                    <ProgressTable
                        rt={rt}
                        project={project}
                        byPortion={reportBy === `portion`}
                        beginDate={startDate}
                        endDate={endDate}
                    />
                </div>
            </div>
        )
    }
}

export default observer(ProgressReport)
